/**
 * Parser for weave operations - extracts frontmatter and embed directives
 */

import type { Variable, ParsedFrontmatter, EmbedDirective } from './types.js';

/**
 * Regex patterns used in parsing
 */

// Matches the opening of a vars block: <!-- vars (with optional whitespace)
const VARS_OPEN_REGEX = /^\s*<!--\s*vars\s*$/;

// Matches the closing of a vars block: --> (with optional whitespace)
const VARS_CLOSE_REGEX = /^\s*-->\s*$/;

// Matches a variable line: KEY=value or @KEY=value
// Key must start with letter or underscore, followed by alphanumeric or underscore
const VAR_LINE_REGEX = /^\s*(@?)([A-Za-z_][A-Za-z0-9_]*)\s*=\s*(.*?)\s*$/;

// Matches an embed directive; group 1 is the whole directive body (everything
// between `embed:` and `-->`). The body is split on `|` into the component and
// any number of target / param groups (see parseEmbedDirective).
// - <!-- embed: component.md -->
// - <!-- embed: component.md | target.sh -->
// - <!-- embed: component.md | a.sh,b.sh,c.sh -->
// - <!-- embed: component.md | target.sh | PORT=8080, HOST={{SERVER_HOST}} -->
// - <!-- embed: component.md | PORT=8080 -->            (params only, docs-only)
const EMBED_REGEX = /^\s*<!--\s*embed:\s*([^>]*?)\s*-->\s*$/;

// A single param assignment: NAME=value. The value (group 2) may be empty or
// contain `{{VAR}}` placeholders that remap onto a parent variable.
const PARAM_ASSIGN_REGEX = /^([A-Za-z_][A-Za-z0-9_]*)\s*=([\s\S]*)$/;

/**
 * Decide whether a `|`-delimited directive group carries params (`NAME=...`)
 * rather than target script names. A group is params when its first
 * comma-separated entry looks like an assignment.
 */
function isParamGroup(group: string): boolean {
  const first = group.split(',')[0]?.trim() ?? '';
  return PARAM_ASSIGN_REGEX.test(first);
}

/**
 * Parse frontmatter variables from markdown content
 *
 * Frontmatter format:
 * <!-- vars
 * KEY=value
 * @DYNAMIC_KEY=value
 * -->
 *
 * @param content - The markdown content to parse
 * @returns Parsed variables and content without frontmatter
 */
export function parseFrontmatter(content: string): ParsedFrontmatter {
  const lines = content.split('\n');
  const variables: Variable[] = [];
  const outputLines: string[] = [];

  let inVarsBlock = false;

  for (const line of lines) {
    // Check for start of vars block
    if (VARS_OPEN_REGEX.test(line)) {
      inVarsBlock = true;
      continue;
    }

    // Check for end of vars block
    if (inVarsBlock) {
      if (VARS_CLOSE_REGEX.test(line)) {
        inVarsBlock = false;
        continue;
      }

      // Parse variable line
      const match = line.match(VAR_LINE_REGEX);
      if (match) {
        const [, dynamicPrefix, name, value] = match;
        variables.push({
          name,
          value,
          isDynamic: dynamicPrefix === '@',
        });
      }
      // Skip other lines in vars block (comments, empty lines)
      continue;
    }

    // Regular content line
    outputLines.push(line);
  }

  return {
    variables,
    contentWithoutFrontmatter: outputLines.join('\n'),
  };
}

/**
 * Parse a single line to check if it's an embed directive
 *
 * Embed formats:
 * <!-- embed: component.md -->              (docs only, strips tangle markers)
 * <!-- embed: component.md | target.sh -->  (redirect tangle blocks to target.sh)
 * <!-- embed: component.md | a.sh,b.sh -->  (redirect to multiple targets)
 * <!-- embed: path/to/comp.md | {{VAR}} --> (target can use variables)
 * <!-- embed: comp.md | init.sh | PORT=8080, HOST={{SERVER_HOST}} --> (params)
 * <!-- embed: comp.md | PORT=8080 -->       (params only, docs-only)
 *
 * The directive body is split on `|`: the first group is the component path,
 * and each remaining group is classified as target script names or params
 * (`NAME=value`) by whether it looks like an assignment.
 *
 * @param line - The line to check
 * @param lineNumber - The line number for error reporting
 * @returns EmbedDirective if line is an embed, null otherwise
 */
export function parseEmbedDirective(line: string, lineNumber: number): EmbedDirective | null {
  const match = line.match(EMBED_REGEX);
  if (!match) {
    return null;
  }

  const groups = match[1].split('|');
  const component = groups.shift()?.trim() ?? '';
  if (!component) {
    return null;
  }

  const targets: string[] = [];
  let params: Record<string, string> | null = null;

  for (const group of groups) {
    const trimmed = group.trim();
    if (!trimmed) continue;

    if (isParamGroup(trimmed)) {
      params = params ?? {};
      for (const entry of trimmed.split(',')) {
        const assign = entry.trim().match(PARAM_ASSIGN_REGEX);
        if (assign) {
          params[assign[1]] = assign[2].trim();
        }
      }
    } else {
      for (const target of trimmed.split(',').map(part => part.trim()).filter(Boolean)) {
        targets.push(target);
      }
    }
  }

  return {
    component,
    target: targets.length ? targets.join(',') : null,
    params,
    line: lineNumber,
  };
}

/**
 * Convert variables array to a record for easy lookup
 */
export function variablesToRecord(variables: Variable[]): Record<string, string> {
  const record: Record<string, string> = {};
  for (const v of variables) {
    record[v.name] = v.value;
  }
  return record;
}
